/**
 * Benefit definitions for Dental PPO
 *
 * Dental PPO uses explicit fields for In-Network and Out-of-Network benefits
 * instead of the generic 'networkDependent' flag, as the API structure
 * separates them into specific network benefit objects.
 *
 * Uses builder functions for standard type/amount pairs following the same
 * pattern as accident, basic-life, and other LOCs.
 */

import {
  addNotDisclosedAndNotOffered,
  BaseBenefitPlanPolicyPeriod,
  EmployerContributionEnum,
  extendEnumWithNotDisclosed,
  extendEnumWithNotDisclosedAndNotOffered,
  FundingTypeEnum,
  IndividualDeductibleEnum,
  OutOfNetworkReimbursementEnum,
  RfpAnnualOpenEnrollmentEnum,
  RfpDeductibleDurationEnum,
  RfpDentalServiceCategoryEnum,
  RfpDollarBenefitEnum,
  RfpInclusionStatusEnum,
  RfpOrthoAgeLimitType,
  RfpPercentageBenefitTypeEnum,
  RateGuaranteeTypeEnum,
} from "@platform/openapi-types";
import { ICellEditorParams } from "ag-grid-enterprise";

import { planNameRow } from "../shared/definitions/planNameRow";
import { BenefitDefinitions } from "../../benefits/benefit.types";
import {
  createEnumFormatter,
  createValueFormatter,
} from "../../components/PlanEditor/utilities/enumFormatters";
import * as sharedCellSpecs from "../shared/cellSpecs/common";
import { createDisabledAwareTextFormatter } from "../shared/cellSpecs/formatters";
import {
  deductibleWaivedPreventativeLabels,
  employerContributionLabels,
} from "../shared/definitions";
import {
  buildCommissionBenefits,
  buildTypeAmountBenefitPair,
  buildTypeMultipleAmountsBenefit,
} from "../shared/definitions/builders";

// Pre-build commission definitions to override isRequired on type fields
const commissionDefs = buildCommissionBenefits();

type CellEditorParams = Partial<
  ICellEditorParams & {
    allowTyping?: boolean;
    values?: string[];
    min?: number;
    max?: number;
    precision?: number;
  }
>;

export const dentalPpoBenefitDefinitions: BenefitDefinitions = {
  currentCarrierName: {
    label: "Current Carrier",
    description: "Name of the current insurance carrier",
    cell: sharedCellSpecs.currentCarrierName,
    isRequired: true,
    networkDependent: false,
    benefitPlanLevel: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  ...commissionDefs,
  currentCommissionType: {
    ...commissionDefs["currentCommissionType"],
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  requestedCommissionType: {
    ...commissionDefs["requestedCommissionType"],
    isRequired: true,
  },
  eligibilityClasses: {
    label: "Eligibility Class",
    mpConfigKey: "eligibility_class",
    description: "Benefit classes assigned to this benefit plan group",
    cell: sharedCellSpecs.eligibilityClasses,
    benefitPlanLevel: true,
    isRequired: true,
  },
  name: planNameRow,
  fundingType: {
    label: "Funding Type",
    description: "Funding arrangement for the benefit plan",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        allowTyping: true,
        values: Object.values(FundingTypeEnum),
        formatValue: createEnumFormatter(),
      },
      valueFormatter: createValueFormatter(),
    },
    isRequired: true,
    requiredOnCurrentCoverageOnly: true,
    networkDependent: false,
    benefitPlanLevel: true,
  },
  networkName: {
    label: "Network",
    mpConfigKey: "network",
    description: "Name of the dental network",
    cell: sharedCellSpecs.networkName,
    networkDependent: true,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "individualDeductible",
    typeLabel: "Individual Deductible",
    networkDependent: true,
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      IndividualDeductibleEnum,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Type of individual deductible",
    amountDescription: "Individual deductible amount",
    disabledValues: addNotDisclosedAndNotOffered([]),
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "familyDeductible",
    typeLabel: "Family Deductible",
    networkDependent: true,
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues:
      extendEnumWithNotDisclosedAndNotOffered(RfpDollarBenefitEnum),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Type of family deductible",
    amountDescription: "Family deductible amount",
    disabledValues: addNotDisclosedAndNotOffered(["notIncluded"]),
  }),
  isDeductibleWaivedPreventative: {
    label: "Deductible Waived for Preventive",
    description: "Is deductible waived for preventive services",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(RfpInclusionStatusEnum),
        formatValue: createEnumFormatter(deductibleWaivedPreventativeLabels),
      } as CellEditorParams,
      valueFormatter: createValueFormatter(deductibleWaivedPreventativeLabels),
    },
    networkDependent: false,
  },
  deductibleDuration: {
    label: "Deductible Duration",
    description: "Duration of the deductible",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpDeductibleDurationEnum,
        ),
      } as CellEditorParams,
    },
    networkDependent: false,
  },
  // Extended (non-v5) fields (CD-1659/CD-1660). Backend fields are plain
  // strings for now (RFC-004), not yet enums, so their dropdown values and
  // labels are defined directly in sharedCellSpecs rather than sourced from
  // a generated OpenAPI enum (CD-2325).
  preventiveAdvantage: {
    mpOnly: true,
    label: "Preventive Advantage",
    mpConfigKey: "preventive_advantage",
    description: "Preventive advantage benefit details",
    cell: sharedCellSpecs.preventiveAdvantageDropdown,
    networkDependent: false,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "annualMaximum",
    typeLabel: "Annual Maximum",
    networkDependent: true,
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues:
      extendEnumWithNotDisclosedAndNotOffered(RfpDollarBenefitEnum),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Type of annual maximum",
    amountDescription: "Annual maximum amount",
    disabledValues: addNotDisclosedAndNotOffered(["notIncluded"]),
    isRequired: true,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "preventativeType1",
    typeLabel: "Preventive - Type 1",
    networkDependent: true,
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpPercentageBenefitTypeEnum,
    ),
    amountCell: sharedCellSpecs.percentageAmount,
    typeDescription: "Type of preventive coverage",
    amountDescription: "Preventive coverage percentage",
    disabledValues: addNotDisclosedAndNotOffered(["notIncluded"]),
    isRequired: true,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "basicType2",
    typeLabel: "Basic - Type 2",
    networkDependent: true,
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpPercentageBenefitTypeEnum,
    ),
    amountCell: sharedCellSpecs.percentageAmount,
    typeDescription: "Type of basic coverage",
    amountDescription: "Basic coverage percentage",
    disabledValues: addNotDisclosedAndNotOffered(["notIncluded"]),
    isRequired: true,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "majorType3",
    typeLabel: "Major - Type 3",
    networkDependent: true,
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpPercentageBenefitTypeEnum,
    ),
    amountCell: sharedCellSpecs.percentageAmount,
    typeDescription: "Type of major coverage",
    amountDescription: "Major coverage percentage",
    disabledValues: addNotDisclosedAndNotOffered(["notIncluded"]),
    isRequired: true,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "orthoType4",
    typeLabel: "Ortho - Type 4",
    networkDependent: true,
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpPercentageBenefitTypeEnum,
    ),
    amountCell: sharedCellSpecs.percentageAmount,
    typeDescription: "Type of orthodontic coverage",
    amountDescription: "Orthodontic coverage percentage",
    disabledValues: addNotDisclosedAndNotOffered(["notIncluded"]),
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "orthoAgeLimit",
    typeLabel: "Ortho Age Limit",
    networkDependent: true,
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues:
      extendEnumWithNotDisclosedAndNotOffered(RfpOrthoAgeLimitType),
    amountLabel: "Age",
    valueSuffix: "Age",
    amountCell: sharedCellSpecs.numberField,
    typeDescription: "Type of orthodontic age limit",
    amountDescription: "Orthodontic age limit",
    disabledValues: addNotDisclosedAndNotOffered([
      "adultAndChildren",
      "childrenOnlyAgeNotDisclosed",
      "notApplicable",
      "notIncluded",
    ]),
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "orthoMaximum",
    typeLabel: "Ortho Maximum",
    networkDependent: true,
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues:
      extendEnumWithNotDisclosedAndNotOffered(RfpDollarBenefitEnum),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Type of orthodontic maximum",
    amountDescription: "Orthodontic maximum amount",
    disabledValues: addNotDisclosedAndNotOffered(["notIncluded"]),
  }),
  oralExams: {
    mpOnly: true,
    label: "Oral Exams",
    mpConfigKey: "oral_exams",
    description: "Oral exams coverage details",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      } as CellEditorParams,
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      },
    },
    networkDependent: false,
  },
  oralExamFrequency: {
    mpOnly: true,
    label: "Oral Exam Frequency",
    mpConfigKey: "oral_exam_frequency",
    description: "Frequency at which oral exams are covered",
    cell: sharedCellSpecs.dentalTwiceYearlyFrequencyDropdown,
    networkDependent: false,
  },
  cleaningFrequency: {
    mpOnly: true,
    label: "Cleaning Frequency",
    mpConfigKey: "cleaning_frequency",
    description: "Frequency at which cleanings are covered",
    cell: sharedCellSpecs.dentalTwiceYearlyFrequencyDropdown,
    networkDependent: false,
  },
  bitewingXRays: {
    mpOnly: true,
    label: "Bitewing X-Rays",
    mpConfigKey: "bitewing_x_rays",
    description: "Bitewing X-Rays coverage details",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      } as CellEditorParams,
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      },
    },
    networkDependent: false,
  },
  fullMouthXRays: {
    mpOnly: true,
    label: "Full Mouth X-rays",
    mpConfigKey: "full_mouth_x_rays",
    description: "Full mouth X-rays coverage details",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      } as CellEditorParams,
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      },
    },
    networkDependent: false,
  },
  fullMouthXRaysFrequency: {
    mpOnly: true,
    label: "Full Mouth X-Rays Frequency",
    mpConfigKey: "full_mouth_x_rays_frequency",
    description: "Frequency at which full mouth X-rays are covered",
    cell: sharedCellSpecs.fullMouthXRaysFrequencyDropdown,
    networkDependent: false,
  },
  sealants: {
    label: "Sealants",
    mpConfigKey: "sealants",
    description: "Coverage category for sealants",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      } as CellEditorParams,
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      },
    },
    networkDependent: true,
  },
  fluorideTreatments: {
    mpOnly: true,
    label: "Fluoride Treatments",
    mpConfigKey: "fluoride_treatments",
    description: "Fluoride treatments coverage details",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      } as CellEditorParams,
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      },
    },
    networkDependent: false,
  },
  spaceMaintainers: {
    label: "Space Maintainers",
    mpConfigKey: "space_maintainers",
    description: "Coverage category for space maintainers",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      } as CellEditorParams,
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      },
    },
    networkDependent: true,
  },
  generalAnesthesia: {
    label: "General Anesthesia",
    mpConfigKey: "general_anesthesia",
    description: "Coverage category for general anesthesia",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      } as CellEditorParams,
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      },
    },
    networkDependent: true,
  },
  compositeWhiteFillings: {
    label: "Composite (White) Fillings",
    mpConfigKey: "composite_white_fillings",
    description: "Coverage category for composite white fillings",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      } as CellEditorParams,
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      },
    },
    networkDependent: true,
  },
  amalgamSilverFillings: {
    mpOnly: true,
    label: "Amalgam (Silver) Fillings",
    mpConfigKey: "amalgam_silver_fillings",
    description: "Amalgam (silver) fillings coverage details",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      } as CellEditorParams,
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      },
    },
    networkDependent: false,
  },
  simpleExtractions: {
    mpOnly: true,
    label: "Simple Extractions",
    mpConfigKey: "simple_extractions",
    description: "Simple extractions coverage details",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      } as CellEditorParams,
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      },
    },
    networkDependent: false,
  },
  endodontics: {
    label: "Endodontics",
    description: "Coverage category for endodontics",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      } as CellEditorParams,
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      },
    },
    networkDependent: true,
  },
  periodonticsNonSurgical: {
    label: "Periodontics (Non-Surgical)",
    description: "Coverage category for periodontics non-surgical",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      } as CellEditorParams,
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      },
    },
    networkDependent: true,
  },
  periodonticsSurgical: {
    label: "Periodontics (Surgical)",
    description: "Coverage category for periodontics surgical",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      } as CellEditorParams,
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      },
    },
    networkDependent: true,
  },
  oralSurgerySimple: {
    label: "Oral Surgery (Simple)",
    mpConfigKey: "oral_surgery_simple",
    description: "Coverage category for oral surgery simple",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      } as CellEditorParams,
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      },
    },
    networkDependent: true,
  },
  oralSurgeryComplex: {
    label: "Oral Surgery (Complex)",
    mpConfigKey: "oral_surgery_complex",
    description: "Coverage category for oral surgery complex",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      } as CellEditorParams,
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      },
    },
    networkDependent: true,
  },
  bridgesDentures: {
    mpOnly: true,
    label: "Bridges & Dentures",
    mpConfigKey: "bridges_dentures",
    description: "Bridges and dentures coverage details",
    cell: sharedCellSpecs.dentalMajorServiceDropdown,
    networkDependent: false,
  },
  bridgesDenturesFrequency: {
    mpOnly: true,
    label: "Bridges & Dentures Frequency",
    mpConfigKey: "bridges_dentures_frequency",
    description: "Frequency at which bridges and dentures are covered",
    cell: sharedCellSpecs.dentalMajorServiceFrequencyDropdown,
    networkDependent: false,
  },
  crowns: {
    mpOnly: true,
    label: "Crowns",
    mpConfigKey: "crowns",
    description: "Crowns coverage details",
    cell: sharedCellSpecs.dentalMajorServiceDropdown,
    networkDependent: false,
  },
  crownsFrequency: {
    mpOnly: true,
    label: "Crowns Frequency",
    mpConfigKey: "crowns_frequency",
    description: "Frequency at which crowns are covered",
    cell: sharedCellSpecs.dentalMajorServiceFrequencyDropdown,
    networkDependent: false,
  },
  implants: {
    label: "Implants",
    mpConfigKey: "implants",
    description: "Coverage category for implants",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      } as CellEditorParams,
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotDisclosedAndNotOffered(
          RfpDentalServiceCategoryEnum,
        ),
      },
    },
    networkDependent: true,
  },
  tmj: {
    mpOnly: true,
    label: "TMJ",
    mpConfigKey: "tmj",
    description: "TMJ coverage details",
    cell: sharedCellSpecs.tmjDropdown,
    networkDependent: false,
  },
  outOfNetworkReimbursement: {
    label: "Out of Network Reimbursement",
    description: "Reimbursement level for out of network services",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          OutOfNetworkReimbursementEnum,
        ),
      } as CellEditorParams,
    },
    isRequired: true,
    networkDependent: false,
  },
  maximumRollover: {
    label: "Maximum Rollover",
    description: "Maximum rollover inclusion status",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(RfpInclusionStatusEnum),
      } as CellEditorParams,
    },
    isRequired: true,
    networkDependent: false,
  },
  missingToothExclusion: {
    label: "Missing Tooth Exclusion",
    mpConfigKey: "missing_tooth_exclusion",
    description: "Missing tooth exclusion status",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(RfpInclusionStatusEnum),
      } as CellEditorParams,
    },
    networkDependent: false,
  },
  workInProgressIncluded: {
    mpOnly: true,
    label: "Work In Progress Included",
    mpConfigKey: "work_in_progress_included",
    description: "Whether work in progress is included",
    cell: sharedCellSpecs.dentalPpoWorkInProgressIncludedDropdown,
    networkDependent: false,
  },
  openOrClosedContract: {
    mpOnly: true,
    label: "Open or Closed Contract",
    mpConfigKey: "open_or_closed_contract",
    description: "Whether the contract is open or closed",
    cell: sharedCellSpecs.dentalPpoOpenOrClosedContractDropdown,
    networkDependent: false,
  },
  // Unlike its sibling extended fields above, eligibilityHours has a
  // confirmed dropdown value list (CD-2325).
  eligibilityHours: {
    mpOnly: true,
    label: "Eligibility Hours",
    mpConfigKey: "eligibility_hours",
    description: "Minimum hours worked per week to be eligible for the plan",
    cell: sharedCellSpecs.eligibilityHoursDropdown,
    networkDependent: false,
  },
  serviceWaitingPeriods: {
    label: "Service Waiting Periods",
    description: "Type of dental service that has a waiting period requirement",
    defaultValue: 1, // setting default value to 1 because there is an error that resets the value if set to null.
    cell: {
      ...sharedCellSpecs.serviceWaitingPeriods,
      cellEditorParams: {
        ...sharedCellSpecs.serviceWaitingPeriods.cellEditorParams,
        linkedFields: ["serviceWaitingPeriodsMonths"],
      } as CellEditorParams,
    },
    isRequired: true,
    networkDependent: false,
    invalidValues: [1],
    acceptableValues: [[]],
  },
  serviceWaitingPeriodsMonths: {
    label: "Amount",
    description:
      "Number of months a member must wait before utilizing the service (1-24)",
    defaultValue: 1,
    nested: true,
    cell: {
      ...sharedCellSpecs.serviceWaitingPeriods,
      cellEditorParams: {
        ...sharedCellSpecs.serviceWaitingPeriods.cellEditorParams,
        linkedFields: ["serviceWaitingPeriods"],
        displayMode: "months",
      } as CellEditorParams,
    },
    networkDependent: false,
  },
  annualOpenEnrollment: {
    label: "Annual Open Enrollment",
    mpConfigKey: "annual_open_enrollment",
    description: "Annual open enrollment availability",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpAnnualOpenEnrollmentEnum,
        ),
      } as CellEditorParams,
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotDisclosedAndNotOffered(
          RfpAnnualOpenEnrollmentEnum,
        ),
      },
    },
    networkDependent: false,
  },
  dependentAgeLimitYears: {
    label: "Dependent Age Limit",
    mpConfigKey: "dependent_age_limit",
    description: "Age limit for dependents",
    cell: {
      ...sharedCellSpecs.numberField,
      cellEditorParams: {
        ...sharedCellSpecs.numberField.cellEditorParams,
        min: 0,
        max: 33,
      } as CellEditorParams,
    },
    networkDependent: false,
    isRequired: true,
  },
  employerContribution: {
    label: "Employer Contribution",
    description: "Employer contribution level",
    cell: {
      ...sharedCellSpecs.employerContribution,
      cellEditorParams: {
        ...sharedCellSpecs.employerContribution.cellEditorParams,
        values: extendEnumWithNotDisclosed(EmployerContributionEnum),
        formatValue: createEnumFormatter(employerContributionLabels),
      } as CellEditorParams,
      valueFormatter: createValueFormatter(employerContributionLabels),
    },
    isRequired: true,
    networkDependent: false,
  },
  participationRequirements: {
    label: "Participation Requirements",
    description: "Participation requirement details for the benefit plan",
    cell: sharedCellSpecs.text,
    networkDependent: false,
    benefitPlanLevel: true,
  },
  ...buildTypeMultipleAmountsBenefit({
    prefix: "rateGuarantee",
    typeLabel: "Rate Guarantee / Rate Caps",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: Object.keys(RateGuaranteeTypeEnum),
    typeDescription:
      "Time frame in which rates are guaranteed to stay the same",
    amountFields: [
      {
        suffix: "Amount",
        label: "Amount",
        cell: sharedCellSpecs.numberField,
        description: "Number of months or years the rate is guaranteed",
        defaultValue: 1,
        disabledValues: [
          RateGuaranteeTypeEnum.notIncluded,
          RateGuaranteeTypeEnum.notOffered,
        ],
      },
      {
        suffix: "Cap",
        label: "Cap",
        cell: {
          ...sharedCellSpecs.text,
          cellEditorParams: {
            maxLength: 1024,
          },
          validation: {
            ...sharedCellSpecs.text.validation,
            maxLength: 1024,
          },
          valueFormatter: createDisabledAwareTextFormatter(),
        },
        description: "Rate cap details following the rate guarantee period",
        disabledValues: [
          RateGuaranteeTypeEnum.notIncluded,
          RateGuaranteeTypeEnum.notOffered,
        ],
      },
    ],
    disabledValues: ["notIncluded", "notOffered"],
  }),
};
